import { match } from 'ts-pattern'
import { z } from 'zod'
import { SupportingDocumentOnly } from 'domain/models/AttendeeFormSchema/AccessRule'
import { MakeAttendeeFormSchemaContext } from 'domain/models/AttendeeFormSchema/MakeAttendeeFormSchemaContext'
import { identity } from 'helpers/constFns'
import { HealthQuestionnaireSchema } from 'services/attendees/AttendeeForm/AttendeeFormSchema/HealthQuestionnaireSchema'
import { UploadedDocumentKeySchema } from 'services/attendees/AttendeeForm/AttendeeFormSchema/UploadedDocumentKeySchema'

const UploadDocumentSchema = (
  type: 'MEDICAL_CERTIFICATE' | 'PARENTAL_AUTHORIZATION'
) =>
  z.object({
    completeType: z.literal(type),
    uploadedDocumentKey: UploadedDocumentKeySchema
  })

const MinorCheckboxAuthorizationSchema = z.object({
  completeType: z.literal('CHECKBOX_AUTHORIZATION'),
  checked: z
    .boolean()
    .refine(identity, 'Vous devez autoriser la participation pour continuer')
})

const NoSupportingDocumentRequiredSchema = z.object({
  completeType: z.literal('NO_SUPPORTING_DOCUMENT_REQUIRED')
})

export const matchGenericSupportingDocumentSchemas = (
  accessRule: Omit<SupportingDocumentOnly, 'type'>,
  ctx: MakeAttendeeFormSchemaContext
) => {
  const isAttendeeMinor = ctx.attendeeAgeStatus === 'MINOR'
  return [
    ...matchGenericMinorSupportingDocumentSchemas(accessRule, isAttendeeMinor),
    ...matchGenericAdultSupportingDocumentSchemas(accessRule, isAttendeeMinor)
  ]
}

const matchGenericAdultSupportingDocumentSchemas = (
  accessRule: Omit<SupportingDocumentOnly, 'type'>,
  isAttendeeMinor: boolean | undefined
) => {
  return isAttendeeMinor === true
    ? []
    : match(accessRule)
        .with(
          { acceptedAdultSupportingDocument: 'MEDICAL_CERTIFICATE' },
          ({ acceptedAdultSupportingDocument }) => [
            UploadDocumentSchema(acceptedAdultSupportingDocument)
          ]
        )
        .with({ acceptedAdultSupportingDocument: 'NONE' }, () => [
          NoSupportingDocumentRequiredSchema
        ])
        .exhaustive()
}
export const matchGenericMinorSupportingDocumentSchemas = (
  accessRule: Omit<SupportingDocumentOnly, 'type'>,
  isAttendeeMinor: boolean | undefined
) => {
  return isAttendeeMinor === false
    ? []
    : match(accessRule)
        .with(
          { acceptedMinorSupportingDocument: 'HEALTH_QUESTIONNAIRE' },
          () => [HealthQuestionnaireSchema]
        )
        .with(
          { acceptedMinorSupportingDocument: 'CHECKBOX_AUTHORIZATION' },
          () => [MinorCheckboxAuthorizationSchema]
        )
        .with(
          { acceptedMinorSupportingDocument: 'PARENTAL_AUTHORIZATION' },
          ({ acceptedMinorSupportingDocument }) => [
            UploadDocumentSchema(acceptedMinorSupportingDocument)
          ]
        )
        .with({ acceptedMinorSupportingDocument: 'NONE' }, () => [
          NoSupportingDocumentRequiredSchema
        ])
        .exhaustive()
}
